import { PolicyLayout, useSettings, ConfigField } from "@/components/PolicyLayout";

export default function PrivacyPolicy() {
  const s = useSettings();
  return (
    <PolicyLayout title="Privacy Policy" kicker="How we handle your data">
      <p>{s.brand} ("we", "us", or "our") respects the privacy of every visitor and customer. This Privacy Policy explains what personal information we collect, why we collect it, and how we protect it when you use our website or purchase from us.</p>

      <h2>1. Information we collect</h2>
      <h3>When you place an order or create an account</h3>
      <ul>
        <li>Full name</li>
        <li>Email address</li>
        <li>Mobile phone number</li>
        <li>Shipping address (house/flat number, area, landmark, city, state, pincode)</li>
        <li>Order history and preferences</li>
      </ul>
      <h3>When you make a payment</h3>
      <p>Payments on our website are processed by third-party payment gateway providers (currently Razorpay and PhonePe). {s.brand} <b>does not store your credit card number, debit card number, CVV, UPI PIN, net-banking password, or full UPI ID</b>. We only receive a payment reference (transaction ID) and a success/failure status.</p>
      <h3>Automatically collected</h3>
      <ul>
        <li>Basic device and browser information (via cookies and analytics)</li>
        <li>Pages visited and products viewed, to improve the experience</li>
        <li>IP address, used only for fraud prevention and delivery of the service</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To process, pack, ship and deliver your orders</li>
        <li>To communicate order confirmations, shipping updates and delivery status</li>
        <li>To provide customer support when you contact us</li>
        <li>To send occasional promotional communication — only if you have opted in and always with an unsubscribe option</li>
        <li>To detect and prevent fraudulent transactions</li>
        <li>To comply with legal, tax and regulatory requirements</li>
      </ul>

      <h2>3. Cookies</h2>
      <p>We use cookies and similar technologies to keep you logged in, remember items in your cart, and understand how visitors use the site. You can disable cookies in your browser, but some features (like the shopping cart) may not work correctly.</p>

      <h2>4. Third-party service providers</h2>
      <p>We share the minimum information required with trusted service providers strictly to operate our service:</p>
      <ul>
        <li><b>Payment gateways</b> (Razorpay, PhonePe) — to process your payment securely.</li>
        <li><b>Courier / logistics partners</b> — to deliver your order.</li>
        <li><b>Email / SMS providers</b> — to send transactional updates.</li>
        <li><b>Analytics providers</b> — to understand aggregate usage patterns.</li>
      </ul>
      <p>These providers are contractually required to protect your information and use it only for the purpose we share it.</p>

      <h2>5. Data security</h2>
      <p>We use HTTPS encryption across the entire website, industry-standard password hashing (bcrypt) for user accounts, and JWT-based session tokens with expiry. Access to customer data inside our team is restricted on a need-to-know basis.</p>

      <h2>6. Your rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of your account and associated data (subject to legal retention obligations)</li>
        <li>Withdraw consent for marketing communication at any time</li>
      </ul>
      <p>To exercise any of these rights, email us at <ConfigField value={s.email} label="business email"/>.</p>

      <h2>7. Children's privacy</h2>
      <p>Our website is not directed at children under 18. We do not knowingly collect personal information from minors. If you believe a minor has provided us information, please contact us and we will delete it.</p>

      <h2>8. Changes to this policy</h2>
      <p>We may update this Privacy Policy from time to time to reflect changes in our practices or the law. The "Last updated" date at the top will change accordingly. Continued use of the website after an update constitutes acceptance of the revised policy.</p>

      <h2>9. Contact for privacy questions</h2>
      <p>For any privacy-related question or request, please write to <ConfigField value={s.email} label="business email"/> or use our <a href="/contact">Contact page</a>.</p>
    </PolicyLayout>
  );
}
